import React, { useCallback, useEffect, useState, useMemo } from "react";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import {
  UserManagementStatus,
  UserManagementStatusText,
  getUserManagementStatusStyle,
} from "@/constants/enums/user-management-status.enum";
import UserManagementEditCard from "@/pages/UserManagement/components/UserManagementEditProfileCard";
import SendIcon from "@/styles/all-images/icons/send.svg";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import LockIcon from "@styles/all-images/icons/lock.svg";
import UnLockIcon from "@styles/all-images/icons/unlock.svg";
import DocumentIcon from "@styles/all-images/icons/document.svg";
import CheckBlueIcon from "@styles/all-images/icons/checkblue.svg";
import CloseBlueIcon from "@styles/all-images/icons/closeblue.svg";
import FilterIcon from "@styles/all-images/icons/rejectIcon.svg";
import UserManagementAdd from "@/pages/UserManagement/UserManagementAdd";
import UserManagementInvite from "@/pages/UserManagement/components/UserManagementInvite";
import { useNavigate } from "react-router-dom";
import TierConfigurationRejectionReason from "@/pages/TierConfiguration/TierConfigurationRejectionReason";
import FilterModal from "@/components/common/FilterModal";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { usePermissionState } from "@/providers/PermissionProvider";
import { UsersPermissions } from "@/constants/permission-modules/base-permissions";

export type MasterType =
  | "onboardedUsers"
  | "invitedUsers"
  | "editProfileRequests";

export interface MasterDataTableProps {
  type: MasterType;
  fetchData: (
    type: MasterType,
    params: {
      limit?: number;
      page?: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: string;
      companyId?: number | null;
    },
  ) => Promise<any>;
  selectedCompanyId?: number | null;
}

interface AssignedProfile {
  id: number;
  name: string;
}

const MasterDataTable: React.FC<MasterDataTableProps> = ({
  type,
  fetchData,
}) => {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDate } = dateHandler();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [columns, setColumns] = useState<any>([]);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [selectedCompanyProfile, setSelectedCompanyProfile] =
    useState<any>(null);
  const { isPermitted } = usePermissionState();

  const titleNameObject = useMemo(
    () => ({
      onboardedUsers: {
        title: "onboardedUsers",
        display: "Onboarded User",
      },
      invitedUsers: {
        title: "invitedUsers",
        display: "Invited User",
      },
      editProfileRequests: {
        title: "editProfileRequests",
        display: "Profile Update Request",
      },
    }),
    [],
  );

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      return fetchData(type, { ...params, companyId: selectedCompanyId });
    },
    [type, fetchData, selectedCompanyId],
  );

  const handleView = (row: any) => {
    navigate(`/user-management-details/${row.id}`);
  };
  const handleViewDocument = (row: any) => {
    const currentPathName = window.location.pathname;
    navigate(
      `/t3/view-approved-onboarding-documents/${row.id}?from=${currentPathName}`,
    );
  };
  const handleOnboardedUser = async (row: any) => {
    navigate(`/onboarded-user-details/${row.id}`);
  };

  const handleEditProfile = (row: any) => {
    if (row?.companies[0]?.id) {
      setSelectedCompanyProfile(row?.companies[0]?.id);
      setSelectedProfile(row);
      setIsProfileModalOpen(true);
    } else {
      toast.error("No company assigned to this user");
    }
  };

  const handleCloseProfileModal = (type: "close" | "submit") => {
    setIsProfileModalOpen(false);
    if (type === "submit") {
      setForceRefresh(true);
    }
    setSelectedCompanyProfile(null);
  };

  const handleDocument = (row: any) => {
    navigate(`/approved-on-boarding-document/`);
    // navigate(`/approved-on-boarding-document/${row.id}`);
  };

  const getActionColumn = (type: MasterType) => {
    switch (type) {
      case "onboardedUsers":
        return {
          header: "Actions",
          accessor: (row: any) => {
            const status = row.status;
            return (
              <div className="tw-flex tw-items-center">
                {(status === UserManagementStatus.FR ||
                  status === UserManagementStatus.O) && (
                  <>
                    <button
                      onClick={() => handleOnboardedUser(row)}
                      title="View"
                    >
                      <img
                        src={EyeIcon}
                        alt="View"
                        className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                      />
                    </button>
                  </>
                )}
                {/* Blitz In Review - Show Approve/Reject */}
                {(status === UserManagementStatus.BR ||
                  (status === UserManagementStatus.FR &&
                    !(
                      row.user_type?.id === UserTierType.Tier4 &&
                      status === UserManagementStatus.FR
                    ))) &&
                  isPermitted(
                    UsersPermissions.APPROVE_REJECT_USERS_MANAGEMENT,
                  ) && (
                    <>
                      <button
                        onClick={() => handleApprove(row)}
                        title="Approve"
                      >
                        <img
                          src={CheckBlueIcon}
                          alt="Approve"
                          className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                        />
                      </button>
                      <button onClick={() => handleReject(row)} title="Reject">
                        <img
                          src={CloseBlueIcon}
                          alt="Reject"
                          className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                        />
                      </button>
                    </>
                  )}

                {/* Full Onboarding In Review - Show View Details and Approve/Reject */}

                {/* Onboarded - Show Edit, View, and Block/Unblock */}
                {status === UserManagementStatus.O && (
                  <>
                    {/* <button
                      onClick={() => handleOnboardedUser(row)}
                      title="View Details"
                    >
                      <img
                        src={EyeIcon}
                        alt="View"
                        className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-6 tw-max-h-6"
                      />
                    </button> */}
                    {row.user_type?.id === 4 && (
                      <button
                        onClick={() => handleViewDocument(row)}
                        title="View Document"
                      >
                        <img
                          src={DocumentIcon}
                          alt="View Document"
                          className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-6 tw-max-h-6"
                        />
                      </button>
                    )}
                    {isPermitted(UsersPermissions.UPDATE_USERS_MANAGEMENT) && (
                      <button
                        onClick={() => handleBlockUser(row)}
                        title={row.is_active ? "Block User" : "Unblock User"}
                        className={`tw-rounded-full ${row.is_active ? "tw-text-red-500" : "tw-text-blue-500"}`}
                      >
                        <img
                          src={row.is_active ? UnLockIcon : LockIcon}
                          alt={row.is_active ? "Unblock User" : "Block User"}
                          className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-6 tw-max-h-6"
                        />
                      </button>
                    )}
                  </>
                )}
                {/* No actions for Onboarding Pending, Full Onboarding Pending, and Rejected statuses */}
              </div>
            );
          },
        };
      case "invitedUsers":
        return isPermitted(UsersPermissions.SEND_USERS_MANAGEMENT)
          ? {
              header: "Resend",
              accessor: (row: any) => (
                <div className="tw-flex tw-items-center">
                  <button onClick={() => handleSend(row)}>
                    <img
                      src={SendIcon}
                      alt="Share"
                      title="Resend"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                </div>
              ),
            }
          : null;
      case "editProfileRequests":
        return {
          header: "Action",
          accessor: (row: any) => (
            <div className="tw-flex tw-items-center">
              <button onClick={() => handleView(row)} title="View">
                <img
                  src={EyeIcon}
                  alt="View"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            </div>
          ),
        };
      default:
        return null;
    }
  };

  const handleApprove = async (row: any) => {
    try {
      const { authenticatedCall } = actionsHandler("userProfileApprovalStatus");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {
            id: row.id,
            status: 1,
            rejection_reason: null,
          },
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            "Profile update request approved successfully",
        );
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error approving profile update:", error);
    }
  };

  const handleReject = async (row: any) => {
    try {
      setIsRejectionModalOpen(true);
      setSelectedItem(row);
    } catch (error) {
      console.error("Error setting up rejection:", error);
    }
  };

  const handleRejectSubmit = async (rejectionReason: string) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("userProfileApprovalStatus");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {
            id: selectedItem.id,
            status: 2,
            rejection_reason: rejectionReason,
          },
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            "Profile update request rejected successfully",
        );
        setIsRejectionModalOpen(false);
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error rejecting profile update:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (row: any) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("invitationResend");

      const response = await dispatch(
        authenticatedCall({
          payload: {
            email: row.email,
          },
        }),
      );

      if (response.ok) {
        toast.success(response.data?.message || "Invitation sent successfully");
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlockUser = async (row: any) => {
    try {
      const { authenticatedCall } = actionsHandler("updateBlockedStatus");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {
            id: row.id,
            status: !row.is_active,
          },
        }),
      );

      if (response.ok) {
        toast.success(response.data?.message);
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error updating block status:", error);
    }
  };

  const handleSettingColumns = useCallback(() => {
    const columnConfigs = {
      onboardedUsers: [
        {
          header: "Stakeholder Name",
          accessor: (row: any) =>
            row.first_name || row.last_name
              ? `${row.first_name} ${row.last_name}`
              : `${row.name || "-"}`,
          sortable: true,
          sortKey: "name",
        },
        {
          header: "Email",
          accessor: "email",
          sortable: true,
          sortKey: "email",
        },
        {
          header: "Contact Number",
          accessor: "phone",
          sortable: true,
          sortKey: "phone",
        },
        {
          header: "User Type",
          accessor: (row: any) => `${row.user_type?.user_type}`,
          sortable: true,
          sortKey: "user_detail__user_type__id",
        },
        {
          header: "Status",
          accessor: (row: any) => {
            const status = row.status as UserManagementStatus;
            const { bgColor, textColor, dotColor } =
              getUserManagementStatusStyle(status);
            return (
              <li
                className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-xs tw-w-fit ${bgColor} ${textColor}`}
              >
                <span
                  className={`tw-w-2 tw-h-2 tw-rounded-full tw-p-1 ${dotColor}`}
                ></span>
                {UserManagementStatusText[status]}
              </li>
            );
          },
          sortable: true,
          sortKey: "user_detail__status",
        },
        {
          header: "Assigned Profile",
          accessor: (row: any) =>
            row.permission_profiles?.length > 0 ? (
              <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-[100px] tw-max-w-[300px]">
                {row.permission_profiles
                  ?.slice(0, 1)
                  .map((profile: AssignedProfile) => (
                    <span
                      key={profile.id}
                      className="tw-px-2 tw-bg-[#F8FAFC] tw-py-1 tw-border tw-border-[#E9EAEB] tw-rounded-full tw-text-sm tw-text-[#232323]"
                    >
                      {profile.name}
                    </span>
                  ))}
                {row.permission_profiles.length > 1 && (
                  <span className="tw-px-2 tw-bg-[#146c94] tw-py-1 tw-border tw-border-[#fff] tw-rounded-full tw-text-sm tw-text-[#fff] tw-cursor-default custom-chip-bg">
                    +{row.permission_profiles.length - 1}
                  </span>
                )}
                {row.status === UserManagementStatus.O &&
                  isPermitted(UsersPermissions.UPDATE_USERS_MANAGEMENT) && (
                    <button
                      onClick={() =>
                        handleEditProfile({
                          ...row,
                          company_id: row.company_id,
                        })
                      }
                      title="Edit"
                    >
                      <img
                        src={EditIcon}
                        alt="Edit Icon"
                        className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-5 tw-max-h-5"
                      />
                    </button>
                  )}
              </div>
            ) : (
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-px-2 tw-py-1 tw-text-sm tw-text-[#232323]">
                  N/A
                </span>
                {row.status === UserManagementStatus.O && (
                  <button onClick={() => handleEditProfile(row)}>
                    <img
                      src={EditIcon}
                      alt="Edit Icon"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-5 tw-max-h-5"
                    />
                  </button>
                )}
              </div>
            ),
          sortable: false,
        },
      ],
      invitedUsers: [
        {
          header: "Email",
          accessor: "email",
          sortable: true,
          sortKey: "email",
        },
        {
          header: "User Type",
          accessor: (row: any) => `${row.user_type}`, // Access nested user_type
          sortable: true,
          sortKey: "user_type",
        },
        {
          header: "Invited Date & Time",
          accessor: (row: any) => formatDate(row.created_at),
          sortable: true,
          sortKey: "created_at",
        },
      ],
      editProfileRequests: [
        {
          header: "User Name",
          accessor: (row: any) =>
            row.first_name || row.last_name
              ? `${row.first_name} ${row.last_name}`
              : "-",
          sortable: true,
          sortKey: "first_name",
        },
        {
          header: "Email",
          accessor: "email",
          sortable: true,
          sortKey: "system_user__email",
        },
        {
          header: "User Type",
          accessor: (row: any) => `${row.user_type}`, // Access nested user_type
          sortable: true,
          sortKey: "system_user__user_detail__user_type",
        },
        {
          header: "Requested Date & Time",
          accessor: (row: any) => formatDate(row.created_at),
          sortable: true,
          sortKey: "created_at",
        },
      ],
    };

    const baseColumns = columnConfigs[type];
    const actionColumn = getActionColumn(type);

    setColumns(actionColumn ? [...baseColumns, actionColumn] : baseColumns);
  }, [type]);

  useEffect(() => {
    handleSettingColumns();
    setForceReset(true);
  }, []);

  useEffect(() => {
    setForceReset(true);
  }, [selectedCompanyId]);

  const getTableButtons = (type: MasterType) => {
    switch (type) {
      case "onboardedUsers":
        return {
          addButton: isPermitted(UsersPermissions.CREATE_USERS_MANAGEMENT)
            ? {
                text: "Add Stakeholder",
                icon: PlusIcon,
                onClick: () => {
                  setModalMode("create");
                  setSelectedItem(null);
                  setModalOpen(true);
                },
              }
            : undefined,
          filterButton: {
            text: "Filter",
            icon: FilterIcon,
            onClick: () => handleFilter(),
          },
        };
      case "invitedUsers":
        return {
          addButton: {
            text: "Invite Stakeholder",
            icon: PlusIcon,
            onClick: () => setModalOpen(true),
          },
        };
      case "editProfileRequests":
        return {};
      default:
        return {};
    }
  };

  const handleClosefilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={handleFetch}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        filterButton={getTableButtons(type).filterButton}
        addButton={getTableButtons(type).addButton}
        noDataMessage={`No ${titleNameObject[type].display} found`}
        noSearchDataMessage={`No matching ${titleNameObject[type].display} found`}
      />

      {modalOpen && type === "invitedUsers" && (
        <UserManagementInvite
          onClose={() => setModalOpen(false)}
          onSubmit={() => {
            setForceRefresh(true);
            setModalOpen(false);
          }}
          isModalOpen={modalOpen}
        />
      )}

      {modalOpen && type === "onboardedUsers" && (
        <UserManagementAdd
          onClose={() => setModalOpen(false)}
          onSubmit={() => {
            setForceRefresh(true);
            setModalOpen(false);
          }}
          isStaffModalOpen={modalOpen}
        />
      )}

      {isProfileModalOpen && selectedCompanyProfile && (
        <UserManagementEditCard
          onClose={() => handleCloseProfileModal("close")}
          onSubmit={() => handleCloseProfileModal("submit")}
          staffId={selectedProfile.id}
          selectedCompanyId={selectedCompanyProfile}
          selectedProfiles={
            selectedProfile.permission_profiles?.map(
              (profile: any) => profile.id,
            ) || []
          }
        />
      )}

      {isRejectionModalOpen && (
        <TierConfigurationRejectionReason
          onClose={() => setIsRejectionModalOpen(false)}
          onSubmit={handleRejectSubmit}
          isLoading={isLoading}
        />
      )}

      {isFilterModalOpen && (
        <FilterModal
          onClose={handleClosefilterModal}
          onApply={(selectedCompanyId) => {
            setSelectedCompanyId(selectedCompanyId || 0);
            setIsFilterModalOpen(false);
          }}
          isOpen={isFilterModalOpen}
          currentFilter={selectedCompanyId || 0}
        />
      )}
    </>
  );
};

export default MasterDataTable;
